import {
  ApolloClient,
  ApolloLink,
  ApolloQueryResult,
  DataProxy,
  defaultDataIdFromObject,
  FetchResult,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
  MutationOptions,
  NextLink,
  NormalizedCacheObject,
  ObservableQuery,
  Operation,
  PossibleTypesMap,
  WatchQueryOptions
} from '@apollo/client/core';
import { OperationVariables } from '@apollo/client/core/types';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { Observable as ApolloObservable } from '@apollo/client/utilities';
import { AuthenticationService, SessionService } from '@innovation-os-ng/core/auth';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { persistCache, PersistentStorage } from 'apollo3-cache-persist';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { PossibleTypesData } from '../models/PossibleTypesData';

const INVALID_TOKEN_MESSAGES = ['invalid_token', 'invalid_access_token'];

export class GraphqlDBO {
  private readonly restClient: ApolloClient<NormalizedCacheObject>;
  private readonly onErrorCallBack: (error: ErrorResponse) => void;
  private readonly sessionService: SessionService;
  private readonly authenticationService: AuthenticationService;
  private readonly environmentService: EnvironmentService;
  private refreshTokenInProgress = false;
  private readonly tokenRefreshedSource = new BehaviorSubject(null);
  private readonly tokenRefreshed = this.tokenRefreshedSource.asObservable();

  constructor(
    uri: string,
    environmentService: EnvironmentService,
    sessionService: SessionService,
    authenticationService: AuthenticationService,
    authLink?: ApolloLink,
    cacheEntitys?: Array<any>,
    credentials: string = 'include',
    cachePersist?: PersistentStorage<any>,
    onErrorCallBack?: (error: ErrorResponse) => void,
    possibleTypesData?: PossibleTypesData
  ) {
    let httpLink: ApolloLink;

    if (authLink) {
      httpLink = authLink.concat(
        new HttpLink({
          uri: environmentService.getGraphQlUrl(uri),
          credentials
        })
      );
    } else {
      // Create an http link:
      httpLink = new HttpLink({
        uri: environmentService.getGraphQlUrl(uri),
        credentials
      });
    }

    this.onErrorCallBack = onErrorCallBack;
    this.sessionService = sessionService;
    this.authenticationService = authenticationService;
    this.environmentService = environmentService;

    const cacheConfig: InMemoryCacheConfig = {
      dataIdFromObject: (object: any) => {
        /* eslint-disable no-underscore-dangle */
        if (cacheEntitys && cacheEntitys.indexOf(object.__typename) > -1) {
          return `${object.__typename}:${object.uri}`;
        } else {
          return defaultDataIdFromObject(object);
        }
        /* eslint-enable no-underscore-dangle */
      }
    };

    // If a fragmentMatcher was passed in, let's configure the cache to use it
    if (!!possibleTypesData) {
      cacheConfig.possibleTypes = this.introspectionToPossibleTypes(possibleTypesData);
    }

    const cache = new InMemoryCache(cacheConfig);

    if (cachePersist) {
      persistCache({
        cache,
        storage: cachePersist
      });
    }

    httpLink = ApolloLink.from([this.getErrorExtensionsLink(), this.getErrorLink(), httpLink]);

    this.restClient = new ApolloClient({
      link: httpLink,
      cache
    });
  }

  /**
   * Returns the current apollo client
   */
  public getClient(): ApolloClient<NormalizedCacheObject> {
    return this.restClient;
  }

  /**
   * Watches cache store according to options specified
   *
   * @param options
   */
  public defaultWatchQuery<T, TVariables>(options: WatchQueryOptions<TVariables>): ObservableQuery<T, TVariables> {
    return this.restClient.watchQuery<T, TVariables>(options);
  }

  /**
   * Watches cache store according to options and returns zen-observable from @apollo/client/utilities
   *
   * @param options
   */
  public watchQuery<T, TVariables>(options: WatchQueryOptions<TVariables>): ApolloObservable<ApolloQueryResult<T>> {
    return this.restClient.watchQuery<T, TVariables>(options);
  }

  /**
   * Resolves mutation and returns the result wrapped in observable
   *
   * @param options
   */
  public mutate<T, TVariables>(options: MutationOptions<T, TVariables>): Observable<FetchResult<T>> {
    return from(this.restClient.mutate<T, TVariables>(options));
  }

  /**
   * Cache Options
   */

  /**
   * Reads data from the cache store and returns the result
   *
   * @param options
   * @param optimistic
   */
  public readQuery<T, TVariables = OperationVariables>(options: DataProxy.Query<TVariables, T>, optimistic?: boolean): T | null {
    return this.restClient.readQuery(options, optimistic);
  }

  /**
   * Writes data and updates the cache store
   *
   * @param options
   */
  public writeQuery<TData = any, TVariables = OperationVariables>(options: DataProxy.WriteQueryOptions<TData, TVariables>): void {
    this.restClient.writeQuery(options);
  }

  /**
   * Iterates the generated fragment types and structures to match 'PossibleTypesMap'
   *
   * @param possibleTypesData
   * @private
   */
  private introspectionToPossibleTypes(possibleTypesData): PossibleTypesMap {
    const possibleTypes = {};
    // eslint-disable-next-line no-underscore-dangle
    possibleTypesData.__schema.types.forEach((supertype) => {
      if (supertype.possibleTypes) {
        possibleTypes[supertype.name] = supertype.possibleTypes.map((subtype) => subtype.name);
      }
    });
    return possibleTypes;
  }

  /**
   * Add 404 and custom error handling when GraphQL or network error occurs
   *
   * @private
   */
  private getErrorLink(): ApolloLink {
    return onError((error) => {
      const { graphQLErrors, operation, forward, networkError } = error;
      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          if (err.extensions && err.extensions.code === 'UNAUTHENTICATED') {
            return this.handle401Error(forward, operation);
          }
        }
      }

      // here the any type is intentionally referred as we are not
      // getting actual types of networkError
      const networkErrorAsAny = networkError as any;

      if (networkErrorAsAny) {
        if (networkErrorAsAny.statusCode === 401) {
          return this.handle401Error(forward, operation);
        }

        if (
          networkErrorAsAny.result &&
          networkErrorAsAny.result.message &&
          INVALID_TOKEN_MESSAGES.some((expectedResponse) => networkErrorAsAny.result.message.includes(expectedResponse))
        ) {
          return this.handle401Error(forward, operation);
        }
      }

      this.onErrorCallBack(error);
    });
  }

  /**
   * Add extensions with 'status' and 'uri' when the response has error
   *
   * @private
   */
  private getErrorExtensionsLink(): ApolloLink {
    return new ApolloLink((operation, forward) =>
      forward(operation).map((data) => {
        // Fetch context to be able to extract 'status' and 'url'
        const context = operation.getContext();
        const {
          response: { status, url }
        } = context;

        // Check if the response from server contains 'errors'
        if (data.errors) {
          const errors = data.errors.map((error) => {
            // GraphQL allows use of 'extensions' to add custom keys
            const extensions = { status, url };
            return { ...error, extensions };
          });
          return { ...data, errors };
        }
        return data;
      })
    );
  }

  private handle401Error(forward: NextLink, operation: Operation) {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.tokenRefreshedSource.next(null);
      return this.refreshTokenAsApolloObservable().flatMap((_) => this.attachTokenAndForwardTheOperation(forward, operation));
    }
    return this.tokenRefreshedAsApolloObservable().flatMap((_) => this.attachTokenAndForwardTheOperation(forward, operation));
  }

  private tokenRefreshedAsApolloObservable(): ApolloObservable<any> {
    return new ApolloObservable((subscriber) => {
      this.tokenRefreshed
        .pipe(
          filter((result) => result !== null),
          take(1)
        )
        .subscribe((_) => {
          if (subscriber.closed) {
            return;
          }
          subscriber.next(_);
          subscriber.complete();
        });
    });
  }

  private attachTokenAndForwardTheOperation(forward: NextLink, operation: Operation): ApolloObservable<any> {
    this.refreshTokenInProgress = false;
    this.tokenRefreshedSource.next('done');
    const token = this.sessionService.getToken();
    const oldHeaders = operation.getContext().headers;
    operation.setContext({
      headers: {
        ...oldHeaders,
        authorization: `Bearer ${token}`
      }
    });

    return forward(operation);
  }

  private refreshTokenAsApolloObservable(): ApolloObservable<any> {
    return new ApolloObservable((subscriber) => {
      this.sessionService.refreshToken().then(
        (_) => {
          if (subscriber.closed) {
            return;
          }
          subscriber.next(_);
          subscriber.complete();
        },
        (err) => {
          this.refreshTokenInProgress = false;
          subscriber.error(err);
          this.logOutAndRedirectToLoginPage();
        }
      );
    });
  }

  /**
   * Simply logs out the user and redirects to login.
   *
   * @private
   */
  private async logOutAndRedirectToLoginPage(): Promise<void> {
    this.refreshTokenInProgress = false;
    this.sessionService.revokeAuthentication();

    const redirectUrl = this.environmentService.getIdAppUrlWithoutTenantSlug();
    location.replace(redirectUrl);
  }
}
